import { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { ZOOM_TRANSCRIPT_STATUS } from "~/providers/constants/zoomTranscriptStatus";
import { parseZoomVtt } from "~/providers/utils/parseZoomVtt";
import { ZoomTranscriptStatus } from "~/providers/utils/types/zoomMeetingTypes";

interface SessionTranscriptViewerProps {
  transcriptStatus?: ZoomTranscriptStatus | null;
  presignedUrl?: string;
  isLoadingTranscript?: boolean;
}

const transcriptStatusMessages: Record<ZoomTranscriptStatus, string> = {
  IN_PROGRESS: "Transcript is being processed. This may take a few minutes.",
  COMPLETED: "",
  FAILED: "No transcript is available for this session.",
};

export const SessionTranscriptViewer = ({
  transcriptStatus,
  presignedUrl,
  isLoadingTranscript = false,
}: SessionTranscriptViewerProps) => {
  const [rawTranscript, setRawTranscript] = useState("");
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetchingVtt, setIsFetchingVtt] = useState(false);

  const cues = useMemo(() => parseZoomVtt(rawTranscript), [rawTranscript]);

  useEffect(() => {
    if (!presignedUrl) {
      setRawTranscript("");
      setFetchError(null);
      return;
    }

    let isCancelled = false;

    const loadTranscript = async () => {
      setIsFetchingVtt(true);
      setFetchError(null);

      try {
        const response = await fetch(presignedUrl);
        if (!response.ok) {
          throw new Error("Failed to load transcript.");
        }

        const content = await response.text();
        if (!isCancelled) {
          setRawTranscript(content);
        }
      } catch {
        if (!isCancelled) {
          setFetchError("Unable to load transcript content.");
          setRawTranscript("");
        }
      } finally {
        if (!isCancelled) {
          setIsFetchingVtt(false);
        }
      }
    };

    void loadTranscript();

    return () => {
      isCancelled = true;
    };
  }, [presignedUrl]);

  if (
    isLoadingTranscript ||
    isFetchingVtt ||
    transcriptStatus === ZOOM_TRANSCRIPT_STATUS.in_progress
  ) {
    return <Skeleton count={3} />;
  }

  if (
    !transcriptStatus ||
    transcriptStatus === ZOOM_TRANSCRIPT_STATUS.failed ||
    fetchError
  ) {
    return (
      <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">
        {fetchError ?? transcriptStatusMessages.FAILED}
      </div>
    );
  }

  if (!cues.length) {
    return (
      <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">
        {transcriptStatusMessages.FAILED}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-2 text-sm leading-relaxed text-gray-700">
        {cues.map((cue, index) => (
          <div key={`${cue.startTime}-${index}`} className="">
            <div title={`${cue.startTime} - ${cue.endTime}`}>
              {cue.speaker}: {cue.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
